/**
 * PermissionsContext — lädt einmalig die effektiven Rechte des aktuellen
 * Users für den aktiven Mandanten (aus /auth/me/permissions) und stellt sie
 * via Hook `usePermissions()` dem Sidebar/Routing/Buttons zur Verfügung.
 *
 * Reload bei Mandantenwechsel (Header „X-Tenant-ID") oder nach Login (user state).
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/axios';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

const PermissionsContext = createContext(null);

const LEVEL_ORDER = { none: 0, read: 1, write: 2, delete: 3 };

export function PermissionsProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { activeTenantId, loading: tenantLoading } = useTenant();
  const [permissions, setPermissions] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLegacyFallback, setIsLegacyFallback] = useState(false);
  const [isAppOnly, setIsAppOnly] = useState(false);
  const [calendarRoomsOccupancy, setCalendarRoomsOccupancy] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const prevUserId = useRef(undefined);

  const reload = useCallback(async () => {
    // Session via HttpOnly cookie — no localStorage JWTs.
    if (!user) {
      setPermissions({});
      setIsSuperAdmin(false);
      setIsLegacyFallback(false);
      setIsAppOnly(false);
      setCalendarRoomsOccupancy(null);
      setLoaded(true);
      return;
    }
    try {
      const r = await api.get('/auth/me/permissions');
      setPermissions(r.data.effective || {});
      setIsSuperAdmin(!!r.data.is_super_admin || !!user?.is_super_admin);
      setIsLegacyFallback(!!r.data.is_legacy_fallback);
      setIsAppOnly(!!r.data.is_app_only);
      setCalendarRoomsOccupancy(r.data.calendar_rooms_occupancy || null);
    } catch {
      // Keep super-admin flag from /auth/me so the sidebar is not an empty shell
      // when /permissions fails (e.g. transient 401 during cookie settle).
      setPermissions({});
      setIsSuperAdmin(!!user?.is_super_admin);
      setIsLegacyFallback(false);
      setIsAppOnly(false);
      setCalendarRoomsOccupancy(null);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  // Re-fetch after auth + tenant are ready. Invalidate loaded on login so
  // Startfenster does not fetch KPIs with empty (logged-out) permissions.
  useEffect(() => {
    if (authLoading || tenantLoading) return;
    const uid = user?.id ?? null;
    if (uid !== prevUserId.current) {
      prevUserId.current = uid;
      if (uid) setLoaded(false);
    }
    reload();
  }, [reload, user, authLoading, tenantLoading, activeTenantId]);

  useEffect(() => {
    const handler = () => reload();
    window.addEventListener('tenant-switched', handler);
    return () => window.removeEventListener('tenant-switched', handler);
  }, [reload]);

  const can = useCallback((resource, requiredLevel = 'read') => {
    if (isSuperAdmin || isLegacyFallback || user?.is_super_admin) return true;
    const have = permissions[resource] || 'none';
    return LEVEL_ORDER[have] >= LEVEL_ORDER[requiredLevel];
  }, [permissions, isSuperAdmin, isLegacyFallback, user]);

  const level = useCallback((resource) => {
    if (isSuperAdmin || isLegacyFallback || user?.is_super_admin) return 'delete';
    return permissions[resource] || 'none';
  }, [permissions, isSuperAdmin, isLegacyFallback, user]);

  return (
    <PermissionsContext.Provider value={{
      permissions, isSuperAdmin, isLegacyFallback, isAppOnly, calendarRoomsOccupancy, loaded, reload, can, level,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Safe fallback if provider not mounted (e.g. during login page)
    return {
      permissions: {}, isSuperAdmin: false, isLegacyFallback: false, isAppOnly: false,
      calendarRoomsOccupancy: null,
      loaded: true, reload: () => {}, can: () => true, level: () => 'none',
    };
  }
  return ctx;
}
